/**
 * What the flight log says about the fleet, per physical aircraft.
 *
 * Every per-aircraft row is joined through the register's slot history on the
 * server, so an aircraft that moved ports is one row and two aircraft that
 * shared a port are two. Rates are per flight hour, so an aircraft is not
 * condemned for being flown more.
 */

import type { ReactNode } from 'react';

import type { AnalyticsSummary } from '@/api/analytics';

import {
  BAR,
  Bars,
  DANGER,
  dateTime,
  DayColumns,
  Empty,
  hours,
  Meter,
  MUTED,
  num,
  OK,
  Panel,
  pct,
  StatTile,
  Table,
  WARN,
  WHITE,
} from './dataUi';

export type AnalyticsTab = 'outcomes' | 'reliability' | 'activity' | 'links' | 'maintenance' | 'events';

export const ANALYTICS_TABS: Array<{ id: AnalyticsTab; label: string }> = [
  { id: 'outcomes', label: 'OUTCOMES' },
  { id: 'reliability', label: 'RELIABILITY' },
  { id: 'activity', label: 'ACTIVITY' },
  { id: 'links', label: 'LINKS' },
  { id: 'maintenance', label: 'MAINTENANCE' },
  { id: 'events', label: 'EVENTS' },
];

const SEVERITY_TONE: Record<string, string> = {
  emergency: DANGER,
  alert: DANGER,
  critical: DANGER,
  error: DANGER,
  warning: WARN,
};

function Row({ children }: { children: ReactNode }) {
  return <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))' }}>{children}</div>;
}

/** Flights that ended with the aircraft armed and silent. Above every tab,
 * because it is the one ending that says nothing about what happened next. */
export function LinkLostAlert({ data }: { data: AnalyticsSummary }) {
  const alert = data.linkLost;
  if (alert.count === 0) return null;
  return (
    <Panel
      title="LINK LOST IN FLIGHT"
      tone={DANGER}
      subtitle="These flights ended while the aircraft was still armed and had stopped talking. Check each one landed on its failsafe."
      right={
        <span className="tnum ml-auto text-[15px] font-semibold" style={{ color: DANGER }}>
          {alert.count}
        </span>
      }
    >
      <Table
        head={['AIRCRAFT', 'DRONE ID', 'STARTED', 'ENDING']}
        empty=""
        rows={alert.recent.map((r) => [
          r.airframe ?? 'Not identified',
          r.droneId,
          dateTime(r.startTime),
          r.reason.replace('_', ' '),
        ])}
      />
    </Panel>
  );
}

function Outcomes({ data }: { data: AnalyticsSummary }) {
  const t = data.outcomes.totals;
  const cleanRate = t.flights ? t.cleanFlights / t.flights : null;
  const days = data.timeline;
  return (
    <>
      <Panel title="HOW FLIGHTS ENDED" subtitle={`The last ${data.window.days} days.`}>
        <div className="flex flex-wrap gap-x-9 gap-y-4">
          <StatTile label="FLIGHTS" value={String(t.flights)} note={`${hours(t.hours)} FLOWN`} />
          <StatTile
            label="CLEAN"
            value={pct(cleanRate)}
            note="NO ERROR, BREACH OR LOST LINK"
            tone={cleanRate === null ? MUTED : cleanRate >= 0.9 ? OK : cleanRate >= 0.7 ? WARN : DANGER}
          />
          <StatTile
            label="ERRORS PER HOUR"
            value={num(t.errorsPerHour, 2)}
            note={`${t.errors} ERRORS`}
            tone={t.errors ? WARN : WHITE}
          />
          <StatTile
            label="LINK LOST"
            value={String(t.linkLost)}
            tone={t.linkLost ? DANGER : WHITE}
          />
          <StatTile label="BREACHES" value={String(t.breaches)} tone={t.breaches ? WARN : WHITE} />
          <StatTile
            label="MISSIONS FLOWN OUT"
            value={pct(data.outcomes.completion.rate)}
            note={`${data.outcomes.completion.withMission} FLIGHTS WITH A MISSION`}
          />
        </div>
      </Panel>
      <Row>
        <Panel title="ENDINGS">
          <Bars
            empty="No flights in this window."
            data={data.outcomes.byReason.map((r) => ({
              label: r.label,
              value: r.count,
              tone: r.linkLost ? DANGER : BAR,
            }))}
          />
        </Panel>
        <Panel
          title="PER DAY"
          subtitle="Each row on its own scale, so a rare and serious count is not flattened by a common one."
        >
          <div className="flex flex-col gap-1.5">
            <DayColumns label="FLIGHTS" values={days.map((d) => d.flights)} total={String(t.flights)} />
            <DayColumns label="ERRORS" values={days.map((d) => d.errors)} tone={WARN} total={String(t.errors)} />
            <DayColumns
              label="BREACHES"
              values={days.map((d) => d.breaches)}
              tone={WARN}
              total={String(t.breaches)}
            />
            <DayColumns
              label="LINK LOST"
              values={days.map((d) => d.linkLost)}
              tone={DANGER}
              total={String(t.linkLost)}
            />
          </div>
        </Panel>
      </Row>
    </>
  );
}

function Reliability({ data }: { data: AnalyticsSummary }) {
  const r = data.reliability;
  return (
    <>
      <Panel
        title="PER AIRCRAFT"
        subtitle={
          r.unattributed.flights
            ? `${r.unattributed.flights} flight${r.unattributed.flights === 1 ? '' : 's'} (${hours(r.unattributed.hours)}) flown before the aircraft was known are counted in the totals but belong to no row.`
            : 'Every flight in this window resolved to the aircraft that flew it.'
        }
      >
        <Table
          head={['AIRCRAFT', 'MODEL', 'FLIGHTS', 'HOURS', 'ERRORS', 'PER HOUR', 'BREACHES', 'LINK LOST', 'LAST FLOWN']}
          empty="No flights resolved to an aircraft yet."
          rows={r.airframes.map((a) => [
            a.name,
            a.platform ?? 'n/a',
            a.flights,
            a.hours.toFixed(1),
            a.errors,
            num(a.errorsPerHour, 2),
            a.breaches,
            <span key="l" style={{ color: a.linkLost ? DANGER : MUTED }}>
              {a.linkLost}
            </span>,
            dateTime(a.lastFlight),
          ])}
        />
      </Panel>
      <Row>
        <Panel title="PER MODEL" subtitle="Errors per flight hour, so the busier type is not the worse-looking one.">
          <Bars
            empty="No identified models have flown in this window."
            data={r.platforms.map((p) => ({
              label: `${p.platform} (${p.airframes})`,
              value: p.errorsPerHour ?? 0,
              display: p.errorsPerHour === null ? 'n/a' : `${p.errorsPerHour.toFixed(2)}/h`,
              title: `${p.flights} flights, ${p.hours.toFixed(1)} h`,
            }))}
          />
        </Panel>
        <Panel
          title="BY FITTED PART"
          subtitle="Correlation, not cause: a part that looks bad here may simply be on the aircraft that fly the hardest sorties."
        >
          <Table
            head={['PART', 'AIRCRAFT', 'HOURS', 'PER HOUR']}
            empty="No parts with a model recorded have flown in this window."
            rows={data.componentCorrelation.map((c) => [
              `${c.category}: ${c.make} ${c.model}`.trim(),
              c.airframes,
              c.hours.toFixed(1),
              num(c.errorsPerHour, 2),
            ])}
          />
        </Panel>
      </Row>
    </>
  );
}

function Activity({ data }: { data: AnalyticsSummary }) {
  const a = data.activity;
  return (
    <>
      <Row>
        <Panel title="BY TEAM">
          <Bars
            empty="No flights in this window."
            data={a.byTeam.map((t) => ({ label: t.team, value: t.hours, display: hours(t.hours) }))}
          />
        </Panel>
        <Panel title="BY PILOT">
          <Bars
            empty="No flights in this window."
            data={a.byPilot.map((p) => ({ label: p.pilot, value: p.hours, display: hours(p.hours) }))}
          />
        </Panel>
      </Row>
      <Row>
        <Panel title="BY DRONE ID" subtitle="Ports, not aircraft: shown so a busy port can be found, not to judge what flew on it.">
          <Table
            head={['DRONE ID', 'FLIGHTS', 'HOURS', 'LAST']}
            empty="No flights in this window."
            rows={a.byDroneId.map((d) => [d.droneId, d.flights, d.hours.toFixed(1), dateTime(d.lastFlight)])}
          />
        </Panel>
        <Panel title="ON THE BOOKS, NOT FLYING" subtitle="Airframes with no flight in this window.">
          <Table
            head={['AIRCRAFT', 'MODEL', 'LAST SEEN']}
            empty="Every airframe on the register flew in this window."
            rows={a.idleAirframes.map((i) => [i.name, i.platform ?? 'n/a', dateTime(i.lastSeen)])}
          />
        </Panel>
      </Row>
    </>
  );
}

function Links({ data }: { data: AnalyticsSummary }) {
  const l = data.links;
  return (
    <>
      <Panel
        title="LINK QUALITY PER AIRCRAFT"
        subtitle="Worst packet loss the autopilot reported on each flight, and flights that ended with the link gone."
      >
        <Table
          head={['AIRCRAFT', 'FLIGHTS', 'LINK LOST', 'WORST LOSS', 'TYPICAL WORST']}
          empty="No flights resolved to an aircraft yet."
          rows={l.airframes.map((a) => [
            a.name,
            a.flights,
            <span key="l" style={{ color: a.linkLost ? DANGER : MUTED }}>
              {a.linkLost}
            </span>,
            pct(a.worstPacketLoss, 1),
            pct(a.meanWorstPacketLoss, 1),
          ])}
        />
      </Panel>
      <Row>
        <Panel
          title="COMMANDS REFUSED"
          subtitle="Share of each command sent in flight that the aircraft, or this server's own checks, said no to."
        >
          <Bars
            empty="No commands recorded during flights in this window."
            data={l.commands.map((c) => ({
              label: c.command,
              value: c.rejectRate ?? 0,
              display: `${c.rejected} of ${c.sent}`,
              tone: c.rejected ? WARN : BAR,
            }))}
          />
        </Panel>
        <Panel title="WHY">
          <Table
            head={['COMMAND', 'REASON GIVEN', 'COUNT']}
            empty="Nothing refused in this window."
            rows={l.topFailures.map((f) => [f.command, f.reason, f.count])}
          />
        </Panel>
      </Row>
    </>
  );
}

function Maintenance({ data }: { data: AnalyticsSummary }) {
  const m = data.maintenance;
  return (
    <Row>
      <Panel
        title="PART HOURS SINCE FITTED"
        subtitle={`Against a ${m.serviceHours} h service interval. Counted from when each part went on, not from the airframe's first flight, and over all history rather than this window.`}
      >
        {m.components.length === 0 ? (
          <Empty>No parts recorded on any airframe you can see.</Empty>
        ) : (
          <div className="flex flex-col gap-2">
            {m.components.map((c) => (
              <div key={c.componentId} className="flex flex-col gap-1 text-[11px]">
                <div className="flex gap-2">
                  <span style={{ color: WHITE }}>{c.label}</span>
                  <span style={{ color: MUTED }}>{c.airframe}</span>
                  <span className="tnum ml-auto" style={{ color: c.due ? DANGER : MUTED }}>
                    {c.hours.toFixed(1)} h{c.due ? ' DUE' : ''}
                  </span>
                </div>
                <Meter fraction={c.fractionOfService ?? 0} tone={c.due ? DANGER : (c.fractionOfService ?? 0) > 0.8 ? WARN : BAR} />
              </div>
            ))}
          </div>
        )}
      </Panel>
      <Panel
        title="AIRFRAME HOURS"
        subtitle="Logged here, beside what the register says. The register's figure may include hours flown before this system existed."
      >
        <Table
          head={['AIRCRAFT', 'FLIGHTS', 'LOGGED', 'REGISTER']}
          empty="Nothing on the register."
          rows={m.airframes.map((a) => [a.name, a.flights, hours(a.loggedHours), hours(a.registerHours)])}
        />
      </Panel>
    </Row>
  );
}

function Events({ data }: { data: AnalyticsSummary }) {
  const e = data.events;
  return (
    <>
      <Row>
        <Panel title="BY SEVERITY">
          <Bars
            empty="No events in this window."
            data={e.bySeverity.map((s) => ({
              label: s.severity,
              value: s.count,
              tone: SEVERITY_TONE[s.severity.toLowerCase()] ?? BAR,
            }))}
          />
        </Panel>
        <Panel title="MOST FREQUENT">
          <Table
            head={['EVENT', 'SEVERITY', 'COUNT']}
            empty="No events in this window."
            rows={e.byType.map((t) => [t.eventType, t.severity, t.count])}
          />
        </Panel>
      </Row>
      <Panel title="LATEST">
        <Table
          head={['WHEN', 'DRONE ID', 'SEVERITY', 'MESSAGE']}
          empty="No events in this window."
          rows={e.recent.map((r) => [
            dateTime(r.timestamp),
            r.droneId,
            <span key="s" style={{ color: SEVERITY_TONE[r.severity.toLowerCase()] ?? MUTED }}>
              {r.severity}
            </span>,
            r.message ?? r.eventType,
          ])}
        />
      </Panel>
    </>
  );
}

export function AnalyticsTabView({ tab, data }: { tab: AnalyticsTab; data: AnalyticsSummary }) {
  switch (tab) {
    case 'reliability':
      return <Reliability data={data} />;
    case 'activity':
      return <Activity data={data} />;
    case 'links':
      return <Links data={data} />;
    case 'maintenance':
      return <Maintenance data={data} />;
    case 'events':
      return <Events data={data} />;
    case 'outcomes':
    default:
      return <Outcomes data={data} />;
  }
}
